/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { useAccountState } from "../../redux/reducers/accountReducer";
import { getHTTPHeaders, HTTPContentType, useApi } from "../api/useApi";
import { CaseDocument, UserAccount } from "../types";
import { useCaseDocumentState } from "../../redux/reducers/caseDocumentReducer";
import { useCaseDocumentActions } from "../../redux/actions/caseDocumentActions";

export const CaseDocumentApiRoutes = {
    getDownloadDocUrl: (accountId: string, caseId: string, docId: string) => `/api/CaseDocument/account/${accountId}/case/${caseId}/doc/${docId}/download`,
    getCaseDocuments: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}`,
    upsertCaseDocument: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/doc`,
    deleteCaseDocuments: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/docs`,
    purgeCaseDocuments: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/docs/purge`,
}
export type CaseDocumentService = {
    docs: CaseDocument[];
    getInitDocUrlAsync: () => Promise<string | null>;
    getUploadDocUrlAsync: () => Promise<string | null>;
    getCommitDocUrlAsync: () => Promise<string | null>;
    getDownloadDocUrlAsync: (docId: string) => Promise<string>;
    upsertCaseDocumentAsync: (caseDoc: CaseDocument) => Promise<void>;
    deleteCaseDocumentAsync: (docId: string) => Promise<void>;
    deleteCaseDocumentsAsync: (docIds: string[]) => Promise<void>;
    purgeCaseDocumentsAsync: (docIds: string[]) => Promise<void>;
    loadDocumentsAsync: () => Promise<void>;
}
/**
 * This service will load documents for a specific case and provides methods to interact with them
 * @param caseId Id for target case object.
 * @param autoload If true, this service will automatically load documents for the specified case, and referesh them when the account or caseId change. It will also listen for signalR updates for documents.
 * @returns
 */
export const useCaseDocumentService = (caseId: string): CaseDocumentService => {

    //Hooks
    const { primaryAccount } = useAccountState();
    const { account } = primaryAccount as UserAccount;
    const caseDocState = useCaseDocumentState();
    const caseDocActions = useCaseDocumentActions();

    //State
    const [initDocUrl, setInitDocUrl] = useState<string>();
    const [uploadDocUrl, setUploadDocUrl] = useState<string>();
    const [commitDocUrl, setCommitDocUrl] = useState<string>();

    //Api
    const api = useApi<CaseDocument>('api/CaseDocument');

    //Data Calls
    const getInitDocUrlAsync = async (): Promise<string> => {

        if (!!initDocUrl) return initDocUrl;
        else {
            const resp = await fetch('/api/CaseDocument/initDocUrl', {
                method: 'GET',
                headers: getHTTPHeaders(HTTPContentType.text),
            });
            const initUrl = (await resp.text()).trim();

            setInitDocUrl(initUrl!);
            return initUrl;
        }
    }
    const getUploadDocUrlAsync = async (): Promise<string> => {
        if (!!uploadDocUrl) return uploadDocUrl;
        else {
            const resp = await fetch('/api/CaseDocument/uploadDocUrl', {
                method: 'GET',
                headers: getHTTPHeaders(HTTPContentType.text),
            });
            const uploadUrl = (await resp.text()).trim();
            setUploadDocUrl(uploadUrl!);

            return uploadUrl;
        }
    }
    const getCommitDocUrlAsync = async (): Promise<string> => {
        if (!!commitDocUrl) return commitDocUrl;
        else {
            const resp = await fetch('/api/CaseDocument/commitDocUrl', {
                method: 'GET',
                headers: getHTTPHeaders(HTTPContentType.text),
            });
            const commitUrl = (await resp.text()).trim();
            setCommitDocUrl(commitUrl!);

            return commitUrl;
        }
    }
    const getDownloadDocUrlAsync = async (docId: string) => {
        const resp = await fetch(CaseDocumentApiRoutes.getDownloadDocUrl(account.accountId as string, caseId, docId), {
            method: 'GET',
            headers: getHTTPHeaders(HTTPContentType.text),
        });
        return (await resp.text()).trim();
    }
    const upsertCaseDocumentAsync = async (caseDoc: CaseDocument) => {
        caseDocActions.addOrReplaceCaseDocument(caseDoc);
        const updatedDoc = await api.postAsync(caseDoc, CaseDocumentApiRoutes.upsertCaseDocument(account.accountId as string, caseId));
        caseDocActions.addOrReplaceCaseDocument(updatedDoc);
    }
    const deleteCaseDocumentAsync = async (docId: string) => {
        caseDocActions.removeCaseDocument(docId);
        await api.deleteAsync([docId], CaseDocumentApiRoutes.deleteCaseDocuments(account.accountId as string, caseId));
    }
    const deleteCaseDocumentsAsync = async (docIds: string[]) => {
        docIds.forEach(docId =>
            caseDocActions.removeCaseDocument(docId)
        );
        await api.deleteAsync(docIds, CaseDocumentApiRoutes.deleteCaseDocuments(account.accountId as string, caseId));
    }
    const purgeCaseDocumentsAsync = async (docIds: string[]) => {
        docIds.forEach(docId =>
            caseDocActions.removeCaseDocument(docId)
        );
        await api.deleteAsync(docIds, CaseDocumentApiRoutes.purgeCaseDocuments(account.accountId as string, caseId));
    }

    const loadDocumentsAsync = async () => {
        caseDocActions.setCaseDocuments([]);
        const docs = await api.getAsync(CaseDocumentApiRoutes.getCaseDocuments(account.accountId as string, caseId));
        caseDocActions.setCaseDocuments(docs);
    }
    
    return {
        docs: caseDocState?.documents || [],
        getInitDocUrlAsync,
        getUploadDocUrlAsync,
        getCommitDocUrlAsync,
        getDownloadDocUrlAsync,
        upsertCaseDocumentAsync,
        deleteCaseDocumentAsync,
        deleteCaseDocumentsAsync,
        purgeCaseDocumentsAsync,
        loadDocumentsAsync
    }
}